import { Children, forwardRef, isValidElement, useEffect, useLayoutEffect, useId, useRef, useState } from 'react'
import ReactSelect from 'react-select'
import './fields.css'

// The existing react-select contract remains available for Dropdown callers.
const StyledSelect = forwardRef(function StyledSelect({ className = '', classNamePrefix, styles, error, ...props }, ref) {
    return <ReactSelect menuPortalTarget={typeof document !== 'undefined' ? document.body : null} menuPosition="fixed" {...props} ref={ref} className={'uiSelect ' + (error ? 'uiSelect--error ' : '') + className} classNamePrefix="uiSelect" styles={{ ...styles, menuPortal: (base, state) => ({ ...base, ...styles?.menuPortal?.(base, state), zIndex: 12000 }) }} />
})
function optionRows(children, disabled = false) {
    return Children.toArray(children).flatMap(child => {
        if (!isValidElement(child)) return []
        if (child.type === 'option') {
            const label = Children.toArray(child.props.children).join('')
            return [{ value: String(child.props.value ?? label), label, isDisabled: disabled || child.props.disabled, hidden: child.props.hidden }]
        }
        if (child.type === 'optgroup') return [{ label: child.props.label, options: optionRows(child.props.children, disabled || child.props.disabled) }]
        return optionRows(child.props.children, disabled)
    })
}
const NativeSelect = forwardRef(function NativeSelect({ children, className = '', style, id, error, ...props }, forwardedRef) {
    const generatedId = useId()
    const fieldId = id || generatedId
    const native = useRef(null)
    const custom = useRef(null)
    const [, refresh] = useState(0)
    const [inheritedDisabled, setInheritedDisabled] = useState(false)
    useLayoutEffect(() => { setInheritedDisabled(!!native.current?.matches(':disabled')) }, [props.disabled])
    const options = optionRows(children)
    const flat = options.flatMap(option => option.options || [option])
    const value = props.value !== undefined ? props.value : native.current?.value ?? props.defaultValue ?? flat[0]?.value ?? ''
    const selected = props.multiple ? flat.filter(option => Array.from(value || []).map(String).includes(option.value)) : flat.find(option => option.value === String(value)) || null
    useEffect(() => {
        const form = native.current?.form
        const reset = () => window.setTimeout(() => refresh(version => version + 1), 0)
        form?.addEventListener('reset', reset)
        return () => form?.removeEventListener('reset', reset)
    }, [])
    return <span className={'uiNativeSelect ' + className} style={style}>
        <select {...props} id={fieldId + '-native'} aria-hidden="true" aria-label={undefined} aria-labelledby={undefined} tabIndex={-1} className="uiNativeBacking" ref={element => { native.current = element; if (typeof forwardedRef === 'function') forwardedRef(element); else if (forwardedRef) forwardedRef.current = element }} onChange={event => { props.onChange?.(event); refresh(version => version + 1) }} onFocus={event => { custom.current?.focus(); props.onFocus?.(event) }}>{children}</select>
        <StyledSelect ref={custom} inputId={fieldId} aria-label={props['aria-label']} aria-labelledby={props['aria-labelledby']} aria-describedby={props['aria-describedby']} error={error} isDisabled={props.disabled || inheritedDisabled} isMulti={props.multiple} isSearchable={false} isClearable={false} options={options.filter(option => !option.hidden)} value={selected} onBlur={props.onBlur} onChange={option => {
            const element = native.current
            if (element.matches(':disabled')) return
            if (props.multiple) {
                const values = (option || []).map(item => item.value)
                Array.from(element.options).forEach(item => { item.selected = values.includes(item.value) })
            } else element.value = option?.value ?? ''
            element.dispatchEvent(new Event('change', { bubbles: true }))
        }} />
    </span>
})
const Select = forwardRef(function Select({ helperText, error, ...props }, ref) {
    const helperId = useId()
    const describedBy = [props['aria-describedby'], helperText ? helperId : ''].filter(Boolean).join(' ') || undefined
    const control = props.children !== undefined ? <NativeSelect {...props} error={error} aria-describedby={describedBy} ref={ref} /> : <StyledSelect {...props} error={error} aria-describedby={describedBy} ref={ref} />
    return helperText ? <>{control}<span id={helperId} className={error ? 'uiFieldError' : 'uiFieldHint'}>{helperText}</span></> : control
})
Select.uiField = true
export default Select
